import { c as _c } from "react/compiler-runtime";
import type { Element } from 'hast';
import Link from 'next/link';
import { memo } from 'react';
import type { Components } from 'react-markdown';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { BundledLanguage } from 'shiki';
import { bundledLanguages } from 'shiki';
import { toast } from 'sonner';
import type { CodeHighlight } from './remark-code-highlight';
import { remarkCodeHighlight } from './remark-code-highlight';
import { CodeBlockActions, CodeBlockCopyButton, CodeBlockFilename, CodeBlockHeader, CodeBlockTitle, CodeBlock as ElementsCodeBlock } from '@/components/ai-elements/code-block';
import { ZoomableMermaid } from '@/components/mermaid-renderer/zoomable-mermaid';
import { cn } from '@/lib/utils';
function extractCodeElement(children: React.ReactNode) {
  if (!children || typeof children !== 'object' || !('props' in children)) {
    return null;
  }
  return children as React.ReactElement<{
    children?: React.ReactNode;
    className?: string;
    node?: Element;
  }>;
}
const LANGUAGE_CLASS_REGEX = /language-([\w+-]+)/;
const FILENAME_META_REGEX = /\b(?:filename|file|path|title)=['"]([^'"]+)['"]/i;
const CODE_BLOCK_LANGUAGE_ALIASES: Record<string, BundledLanguage> = {
  bash: 'bash',
  c: 'c',
  clike: 'c',
  css: 'css',
  diff: 'diff',
  docker: 'docker',
  graphql: 'graphql',
  hcl: 'hcl',
  html: 'html',
  java: 'java',
  javascript: 'javascript',
  js: 'javascript',
  json: 'json',
  jsx: 'jsx',
  kotlin: 'kotlin',
  makefile: 'makefile',
  markdown: 'markdown',
  md: 'markdown',
  nginx: 'nginx',
  patch: 'diff',
  php: 'php',
  prisma: 'prisma',
  python: 'python',
  ruby: 'ruby',
  rust: 'rust',
  shell: 'bash',
  shellscript: 'bash',
  sh: 'bash',
  sql: 'sql',
  swift: 'swift',
  toml: 'toml',
  ts: 'typescript',
  tsx: 'tsx',
  typescript: 'typescript',
  xml: 'xml',
  yaml: 'yaml',
  yml: 'yaml',
  zsh: 'bash'
};
function isLikelyBareFilename(value: string) {
  if (value.length === 0 || /\s/.test(value)) {
    return false;
  }
  const hasFileExtension = value.includes('.') && !value.endsWith('.');
  const hasOnlySafeChars = /^[~./\w-]+$/.test(value);
  return hasFileExtension && hasOnlySafeChars;
}
function extractCodeFenceMeta(node: unknown) {
  if (!node || typeof node !== 'object') {
    return '';
  }
  const data = (node as {
    data?: {
      meta?: unknown;
    };
  }).data;
  return typeof data?.meta === 'string' ? data.meta : '';
}
function extractFilenameFromMeta(meta: string) {
  if (!meta) {
    return undefined;
  }
  const keyValueMatch = FILENAME_META_REGEX.exec(meta);
  if (keyValueMatch?.[1]) {
    return keyValueMatch[1].trim();
  }
  const trimmedMeta = meta.trim();
  if (isLikelyBareFilename(trimmedMeta)) {
    return trimmedMeta;
  }
  return undefined;
}
function inferCodeFenceLanguage(code: string) {
  const trimmed = code.trim();
  if (trimmed.length === 0) {
    return 'text';
  }
  const lines = trimmed.split('\n');
  const hasGitDiffHeader = lines.some(line => line.startsWith('diff --git '));
  const hasUnifiedHunk = lines.some(line => line.startsWith('@@ ') && line.includes(' @@', 3));
  const hasFileHeaders = lines.some(line => line.startsWith('--- ')) && lines.some(line => line.startsWith('+++ '));
  const looksLikeDiff = hasGitDiffHeader || hasUnifiedHunk || hasFileHeaders;
  return looksLikeDiff ? 'diff' : 'text';
}
function normalizeCodeBlockLanguage(language: string): BundledLanguage {
  const normalizedLanguage = language.trim().toLowerCase();
  if (!normalizedLanguage) {
    return 'text' as BundledLanguage;
  }
  const aliasedLanguage = CODE_BLOCK_LANGUAGE_ALIASES[normalizedLanguage];
  if (aliasedLanguage) {
    return aliasedLanguage;
  }
  if (normalizedLanguage in bundledLanguages) {
    return normalizedLanguage as BundledLanguage;
  }
  return 'text' as BundledLanguage;
}

// -----------------------------------------------------------------------------
// Markdown Component
// -----------------------------------------------------------------------------

type MarkdownProps = {
  children: string;
  className?: string;
  codeHighlight?: CodeHighlight;
};
const NonMemoizedMarkdown = t0 => {
  const $ = _c(23);
  const {
    children,
    className,
    codeHighlight
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = /```mermaid[^`]*$/m.test(children) && !children.match(/```mermaid[^`]*```/);
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const hasIncompleteMermaid = t1;
  let t2;
  if ($[2] !== children || $[3] !== hasIncompleteMermaid) {
    t2 = hasIncompleteMermaid ? children.replace(/```mermaid\n?([^`]*)$/, "```mermaid\n$1\n```") : children;
    $[2] = children;
    $[3] = hasIncompleteMermaid;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const processedMarkdown = t2;
  let t3;
  if ($[5] !== codeHighlight) {
    t3 = t4 => {
      const {
        node,
        children: children_0,
        ...props
      } = t4;
      const codeElement = extractCodeElement(children_0);
      if (!codeElement) {
        return <pre {...props}>{children_0}</pre>;
      }
      const code = String(codeElement.props?.children ?? "").replace(/\n$/, "");
      const codeClassName = codeElement.props?.className ?? "";
      const match = LANGUAGE_CLASS_REGEX.exec(codeClassName);
      const rawLanguage = (match?.[1] ?? "").trim().toLowerCase();
      const filename = extractFilenameFromMeta(extractCodeFenceMeta(node));
      const highlightedLines = codeElement.props.node?.properties["data-highlighted-lines"];
      if (rawLanguage === "mermaid") {
        return <>{children_0}</>;
      }
      const language = normalizeCodeBlockLanguage(rawLanguage || inferCodeFenceLanguage(code));
      return <ElementsCodeBlock code={code} language={language} highlightedLines={Array.isArray(highlightedLines) && highlightedLines.every(_temp) ? highlightedLines : undefined} highlightTone={codeElement.props.node?.properties["data-highlight-tone"] === "success" ? "success" : "info"}><CodeBlockHeader className={codeHighlight ? "px-2 py-0.5" : undefined}>{filename ? <CodeBlockTitle><CodeBlockFilename>{filename}</CodeBlockFilename></CodeBlockTitle> : null}<CodeBlockActions className={cn("ml-auto", codeHighlight && "my-0 mr-0")}><CodeBlockCopyButton className={codeHighlight ? "size-6" : undefined} onError={_temp2} /></CodeBlockActions></CodeBlockHeader></ElementsCodeBlock>;
    };
    $[5] = codeHighlight;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== hasIncompleteMermaid) {
    t4 = t5 => {
      const {
        node: node_0,
        className: codeClassName_0,
        children: children_1,
        ...props_0
      } = t5;
      const match_0 = LANGUAGE_CLASS_REGEX.exec(codeClassName_0 || "");
      const isInline = !match_0;
      if (isInline) {
        return <code className="inline-block max-w-full rounded-sm border border-border/50 bg-zinc-100 px-1 py-0.5 font-mono text-[0.9em] text-foreground align-baseline wrap-anywhere dark:bg-zinc-800" {...props_0}>{children_1}</code>;
      }
      if (match_0?.[1]?.toLowerCase() === "mermaid") {
        return <div className="my-4 flex justify-center"><ZoomableMermaid content={String(children_1).replace(/\n$/, "")} className="max-w-full overflow-x-auto" isLoading={!children_1?.toString().trim() || hasIncompleteMermaid} /></div>;
      }
      return <code className={codeClassName_0} {...props_0}>{children_1}</code>;
    };
    $[7] = hasIncompleteMermaid;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t3 || $[10] !== t4) {
    t5 = {
      pre: t3,
      code: t4,
      h1: _temp3,
      h2: _temp4,
      h3: _temp5,
      h4: _temp6,
      h5: _temp7,
      h6: _temp8,
      p: _temp9,
      ul: _temp10,
      ol: _temp11,
      li: _temp12,
      input: _temp13,
      strong: _temp14,
      em: _temp15,
      del: _temp16,
      sup: _temp17,
      sub: _temp18,
      mark: _temp19,
      kbd: _temp20,
      a: _temp21,
      img: _temp22,
      details: _temp23,
      summary: _temp24,
      blockquote: _temp25,
      hr: _temp26,
      table: _temp27,
      thead: _temp28,
      tbody: _temp29,
      tr: _temp30,
      th: _temp31,
      td: _temp32
    };
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const components = t5;
  let t6;
  if ($[12] !== className) {
    t6 = cn("markdown-content box-border min-w-0 max-w-full", className);
    $[12] = className;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== codeHighlight) {
    t7 = [remarkGfm, [remarkCodeHighlight, codeHighlight]];
    $[14] = codeHighlight;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== components || $[17] !== processedMarkdown || $[18] !== t7) {
    t8 = <ReactMarkdown remarkPlugins={t7} components={components}>{processedMarkdown}</ReactMarkdown>;
    $[16] = components;
    $[17] = processedMarkdown;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== t6 || $[21] !== t8) {
    t9 = <div className={t6}>{t8}</div>;
    $[20] = t6;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  return t9;
};
export const Markdown = memo(NonMemoizedMarkdown, (prevProps, nextProps) => prevProps.children === nextProps.children && prevProps.className === nextProps.className && prevProps.codeHighlight === nextProps.codeHighlight);
function _temp(line) {
  return typeof line === "number";
}
function _temp2() {
  return toast.error("Failed to copy code");
}
function _temp3(t0) {
  const {
    node: node_1,
    children: children_2,
    ...props_1
  } = t0;
  return <h1 className="text-xl font-semibold mt-6 mb-3 first:mt-0" {...props_1}>{children_2}</h1>;
}
function _temp4(t0) {
  const {
    node: node_2,
    children: children_3,
    ...props_2
  } = t0;
  return <h2 className="text-lg font-semibold mt-5 mb-2" {...props_2}>{children_3}</h2>;
}
function _temp5(t0) {
  const {
    node: node_3,
    children: children_4,
    ...props_3
  } = t0;
  return <h3 className="text-base font-semibold mt-4 mb-2" {...props_3}>{children_4}</h3>;
}
function _temp6(t0) {
  const {
    node: node_4,
    children: children_5,
    ...props_4
  } = t0;
  return <h4 className="text-base font-medium mt-3 mb-1.5" {...props_4}>{children_5}</h4>;
}
function _temp7(t0) {
  const {
    node: node_5,
    children: children_6,
    ...props_5
  } = t0;
  return <h5 className="text-base font-medium mt-3 mb-1.5" {...props_5}>{children_6}</h5>;
}
function _temp8(t0) {
  const {
    node: node_6,
    children: children_7,
    ...props_6
  } = t0;
  return <h6 className="text-base font-medium mt-3 mb-1.5" {...props_6}>{children_7}</h6>;
}
function _temp9(t0) {
  const {
    node: node_7,
    children: children_8,
    ...props_7
  } = t0;
  return <p className="mb-3 min-w-0 max-w-full text-base leading-relaxed wrap-anywhere last:mb-0" {...props_7}>{children_8}</p>;
}
function _temp10(t0) {
  const {
    node: node_8,
    className: className_0,
    children: children_9,
    ...props_8
  } = t0;
  const isTaskList = className_0?.includes("contains-task-list");
  return <ul className={cn("my-2 space-y-1", isTaskList ? "list-none ml-0" : "list-disc list-outside ml-4")} {...props_8}>{children_9}</ul>;
}
function _temp11(t0) {
  const {
    node: node_9,
    children: children_10,
    ...props_9
  } = t0;
  return <ol className="my-2 ml-4 list-decimal list-outside space-y-1" {...props_9}>{children_10}</ol>;
}
function _temp12(t0) {
  const {
    node: node_10,
    className: className_1,
    children: children_11,
    ...props_10
  } = t0;
  const isTaskItem = className_1?.includes("task-list-item");
  return <li className={cn("min-w-0 max-w-full text-base leading-relaxed wrap-anywhere", isTaskItem ? "flex items-start gap-2" : "pl-1")} {...props_10}>{children_11}</li>;
}
function _temp13(t0) {
  const {
    node: node_11,
    type,
    checked,
    disabled,
    ...props_11
  } = t0;
  if (type === "checkbox") {
    return <input type="checkbox" checked={checked} disabled={disabled} className="mt-1 h-3.5 w-3.5 rounded border-border accent-primary" {...props_11} />;
  }
  return <input type={type} checked={checked} disabled={disabled} {...props_11} />;
}
function _temp14(t0) {
  const {
    node: node_12,
    children: children_12,
    ...props_12
  } = t0;
  return <strong className="font-semibold" {...props_12}>{children_12}</strong>;
}
function _temp15(t0) {
  const {
    node: node_13,
    children: children_13,
    ...props_13
  } = t0;
  return <em className="italic" {...props_13}>{children_13}</em>;
}
function _temp16(t0) {
  const {
    node: node_14,
    children: children_14,
    ...props_14
  } = t0;
  return <del className="line-through text-muted-foreground" {...props_14}>{children_14}</del>;
}
function _temp17(t0) {
  const {
    node: node_15,
    children: children_15,
    ...props_15
  } = t0;
  return <sup className="text-xs align-super" {...props_15}>{children_15}</sup>;
}
function _temp18(t0) {
  const {
    node: node_16,
    children: children_16,
    ...props_16
  } = t0;
  return <sub className="text-xs align-sub" {...props_16}>{children_16}</sub>;
}
function _temp19(t0) {
  const {
    node: node_17,
    children: children_17,
    ...props_17
  } = t0;
  return <mark className="bg-yellow-200 dark:bg-yellow-900/50 px-1 rounded-sm" {...props_17}>{children_17}</mark>;
}
function _temp20(t0) {
  const {
    node: node_18,
    children: children_18,
    ...props_18
  } = t0;
  return <kbd className="inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-mono font-medium bg-muted border border-border rounded shadow-sm" {...props_18}>{children_18}</kbd>;
}
function _temp21(t0) {
  const {
    node: node_19,
    children: children_19,
    ...props_19
  } = t0;
  return <Link className="break-all text-blue-600 underline underline-offset-2 decoration-blue-600/30 hover:decoration-blue-600 dark:text-blue-400 dark:decoration-blue-400/30 dark:hover:decoration-blue-400" target="_blank" rel="noreferrer" {...props_19}>{children_19}</Link>;
}
function _temp22(t0) {
  const {
    node: node_20,
    src,
    alt,
    ...props_20
  } = t0;
  if (!src) {
    return null;
  }
  return <img src={src} alt={alt || ""} className="max-w-full rounded-lg my-4" loading="lazy" {...props_20} />;
}
function _temp23(t0) {
  const {
    node: node_21,
    children: children_20,
    ...props_21
  } = t0;
  return <details className="my-3 rounded-lg border border-border bg-muted/30 p-3 open:bg-muted/50" {...props_21}>{children_20}</details>;
}
function _temp24(t0) {
  const {
    node: node_22,
    children: children_21,
    ...props_22
  } = t0;
  return <summary className="cursor-pointer font-medium select-none" {...props_22}>{children_21}</summary>;
}
function _temp25(t0) {
  const {
    node: node_23,
    children: children_22,
    ...props_23
  } = t0;
  return <blockquote className="my-3 border-l-4 border-muted-foreground/30 pl-4 italic text-muted-foreground" {...props_23}>{children_22}</blockquote>;
}
function _temp26(t0) {
  const {
    node: node_24,
    ...props_24
  } = t0;
  return <hr className="my-4 border-border" {...props_24} />;
}
function _temp27(t0) {
  const {
    node: node_25,
    children: children_23,
    ...props_25
  } = t0;
  return <div className="my-4 min-w-0 max-w-full overflow-x-auto"><table className="min-w-full text-base border-collapse border border-border" {...props_25}>{children_23}</table></div>;
}
function _temp28(t0) {
  const {
    node: node_26,
    children: children_24,
    ...props_26
  } = t0;
  return <thead className="bg-muted/50" {...props_26}>{children_24}</thead>;
}
function _temp29(t0) {
  const {
    node: node_27,
    children: children_25,
    ...props_27
  } = t0;
  return <tbody {...props_27}>{children_25}</tbody>;
}
function _temp30(t0) {
  const {
    node: node_28,
    children: children_26,
    ...props_28
  } = t0;
  return <tr className="border-b border-border" {...props_28}>{children_26}</tr>;
}
function _temp31(t0) {
  const {
    node: node_29,
    children: children_27,
    ...props_29
  } = t0;
  return <th className="border border-border px-3 py-2 text-left font-semibold" {...props_29}>{children_27}</th>;
}
function _temp32(t0) {
  const {
    node: node_30,
    children: children_28,
    ...props_30
  } = t0;
  return <td className="border border-border px-3 py-2" {...props_30}>{children_28}</td>;
}